"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useQuery, useMutation } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../../../../convex/_generated/api";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  PluginIcon,
  DownloadIcon,
  StarIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  UploadIcon,
  XIcon,
  TagIcon,
  ArrowRightIcon,
} from "@/components/icons";
import type { Id } from "../../../../convex/_generated/dataModel";

function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export default function MyPluginsPage() {
  const router = useRouter();
  const { isSignedIn, isLoaded } = useAuth();
  const [editingPlugin, setEditingPlugin] = useState<Id<"plugins"> | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Id<"plugins"> | null>(null);

  const myPlugins = useQuery(api.plugins.getMyPlugins);
  const removePlugin = useMutation(api.plugins.remove);

  // Redirect if not signed in
  if (isLoaded && !isSignedIn) {
    router.push("/login?redirect=/plugins/my-plugins");
    return null;
  }

  const handleDelete = async (pluginId: Id<"plugins">) => {
    await removePlugin({ pluginId });
    setDeleteConfirm(null);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Navbar />

      <main className="flex-1">
        <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-3 text-primary">
                <PluginIcon className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">
                  My Plugins
                </h1>
                <p className="text-muted">Manage your uploaded plugins</p>
              </div>
            </div>

            <Link href="/plugins/upload">
              <Button>
                <UploadIcon className="mr-2 h-4 w-4" />
                Upload New
              </Button>
            </Link>
          </div>

          {/* Plugin List */}
          <div className="mt-8 space-y-4">
            {myPlugins === undefined ? (
              [...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))
            ) : myPlugins.length === 0 ? (
              <div className="py-20 text-center">
                <PluginIcon className="mx-auto h-12 w-12 text-muted" />
                <h2 className="mt-4 text-xl font-semibold text-foreground">
                  No plugins yet
                </h2>
                <p className="mt-2 text-muted">
                  Upload your first plugin to share with the community!
                </p>
                <Link href="/plugins/upload" className="mt-6 inline-block">
                  <Button>Upload Your First Plugin</Button>
                </Link>
              </div>
            ) : (
              myPlugins.map((plugin) => (
                <Card key={plugin._id}>
                  <CardContent className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <div className="rounded-lg bg-primary/10 p-3 text-primary">
                        <PluginIcon className="h-8 w-8" />
                      </div>
                      <div>
                        <Link
                          href={`/plugins/${plugin.slug}`}
                          className="text-lg font-semibold text-foreground hover:text-primary"
                        >
                          {plugin.name}
                        </Link>
                        <p className="text-sm text-muted">
                          v{plugin.version} &bull; {formatFileSize(plugin.fileSize)}
                        </p>
                        <div className="mt-2 flex items-center gap-4 text-xs text-muted">
                          <span className="flex items-center gap-1">
                            <EyeIcon className="h-3 w-3" />
                            {plugin.viewCount}
                          </span>
                          <span className="flex items-center gap-1">
                            <DownloadIcon className="h-3 w-3" />
                            {plugin.downloadCount}
                          </span>
                          <span className="flex items-center gap-1">
                            <StarIcon className="h-3 w-3" />
                            {plugin.starCount}
                          </span>
                        </div>
                        {plugin.tags.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1">
                            {plugin.tags.slice(0, 3).map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingPlugin(plugin._id)}
                      >
                        <EditIcon className="mr-1 h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-500 hover:bg-red-500/10 hover:text-red-500"
                        onClick={() => setDeleteConfirm(plugin._id)}
                      >
                        <TrashIcon className="mr-1 h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>

                  {/* Delete Confirmation */}
                  {deleteConfirm === plugin._id && (
                    <div className="border-t border-border bg-red-500/5 p-4">
                      <p className="text-sm text-foreground">
                        Are you sure you want to delete <strong>{plugin.name}</strong>?
                        This action cannot be undone.
                      </p>
                      <div className="mt-4 flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteConfirm(null)}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          className="bg-red-500 text-white hover:bg-red-600"
                          onClick={() => handleDelete(plugin._id)}
                        >
                          Delete Plugin
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Edit Form */}
                  {editingPlugin === plugin._id && (
                    <EditPluginForm
                      plugin={plugin}
                      onClose={() => setEditingPlugin(null)}
                    />
                  )}
                </Card>
              ))
            )}
          </div>

          {/* Back Button */}
          <div className="mt-8">
            <Link href="/plugins">
              <Button variant="outline">
                <ArrowRightIcon className="mr-2 h-4 w-4 rotate-180" />
                Back to Plugins
              </Button>
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

// Edit Form Component
function EditPluginForm({
  plugin,
  onClose,
}: {
  plugin: {
    _id: Id<"plugins">;
    name: string;
    shortDescription: string;
    description: string;
    version: string;
    tags: string[];
  };
  onClose: () => void;
}) {
  const [name, setName] = useState(plugin.name);
  const [shortDescription, setShortDescription] = useState(plugin.shortDescription);
  const [description, setDescription] = useState(plugin.description);
  const [version, setVersion] = useState(plugin.version);
  const [tags, setTags] = useState(plugin.tags);
  const [tagInput, setTagInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updatePlugin = useMutation(api.plugins.update);

  const handleAddTag = () => {
    const normalizedTag = tagInput.toLowerCase().trim();
    if (normalizedTag && !tags.includes(normalizedTag) && tags.length < 10) {
      setTags([...tags, normalizedTag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleSave = async () => {
    setError(null);

    if (!name.trim() || !shortDescription.trim() || !description.trim() || !version.trim()) {
      setError("All fields are required");
      return;
    }

    try {
      setIsSaving(true);
      await updatePlugin({
        pluginId: plugin._id,
        name: name.trim(),
        shortDescription: shortDescription.trim(),
        description: description.trim(),
        version: version.trim(),
        tags,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save changes");
      setIsSaving(false);
    }
  };

  return (
    <div className="border-t border-border p-4">
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Name
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground">
              Version
            </label>
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              maxLength={20}
            />
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            Short Description
          </label>
          <Input
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            maxLength={150}
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            Full Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            maxLength={5000}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <p className="mt-1 text-xs text-muted">
            Supports Markdown: **bold**, *italic*, `code`, lists, headers, and more.
          </p>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-foreground">
            Tags
          </label>
          <div className="flex gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddTag())}
              placeholder="Add a tag..."
              maxLength={30}
            />
            <Button type="button" variant="outline" onClick={handleAddTag}>
              Add
            </Button>
          </div>
          {tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-sm text-primary"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="rounded-full p-0.5 hover:bg-primary/20"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-sm text-red-500">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
